import { useTranslation } from 'react-i18next';
import { RefreshRounded } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import moment from 'moment';
import SearchBar from 'shared/components/search-bar/search-bar';
import DateTimeRangeFilterChip from 'shared/components/table-filter-chips/date-time-range-filter-chip/filter-chip-date-time-range';
import FilterChip from 'shared/components/table-filter-chips/filter-chip/filter-chip';
import MultiValueFilterChip from 'shared/components/table-filter-chips/multi-value-filter-chip/multi-value-filter-chip';
import OrganisationUnitFilterChip from 'shared/components/table-filter-chips/organisation-unit-filter-chip/organisation-unit-filter-chip';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import useFaultsHistoryFilters from 'features/faults-history/context/faults-history-filter-hook';
import { useFaultsHistoryContextProvider } from 'features/faults-history/context/faults-history-provider';
import { FaultsHistoryFilterValueType } from 'features/faults-history/domain/models/faults-history-filter';
import styles from 'features/faults-history/views/components/faults-history-filters/faults-history-filters.module.scss';

const FaultsHistoryFilters = () => {
  const { t } = useTranslation('faultsHistory');
  const { refresh } = useFaultsHistoryContextProvider();

  const {
    submitSearch,
    filters,
    categoryOptions,
    selectOption,
    changeSelectedOrganisationUnits,
    selectedCategoryOptions,
    faultTypeOptions,
    selectedFaultTypeOptions,
    selectedOrganisationUnits,
    clearFilters,
    selectedTypeOptions,
    selectedStatusOption,
    setDateFilters,
    statusOptions,
    typeOptions,
  } = useFaultsHistoryFilters();

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <SearchBar onSearchValueChanged={submitSearch} searchValue={filters.searchBarValue} />
        <MultiValueFilterChip
          key={`faults-history-filter-chip-category`}
          options={categoryOptions}
          placeHolder={t('filters.category')}
          selectedOptions={selectedCategoryOptions}
          onOptionsSelected={(keys: string[]) => selectOption(FaultsHistoryFilterValueType.Category, keys)}
        />
        <MultiValueFilterChip
          options={faultTypeOptions}
          placeHolder={t('filters.faultType')}
          selectedOptions={selectedFaultTypeOptions}
          onOptionsSelected={(keys: string[]) => selectOption(FaultsHistoryFilterValueType.FaultType, keys)}
        />
        <MultiValueFilterChip
          options={typeOptions}
          placeHolder={t('filters.type')}
          selectedOptions={selectedTypeOptions}
          onOptionsSelected={(keys: string[]) => selectOption(FaultsHistoryFilterValueType.DeviceType, keys)}
        />
        <OrganisationUnitTreeProvider.All>
          <OrganisationUnitFilterChip
            key={`faults-history-filter-chip-organisation-unit`}
            placeHolder={t('filters.location')}
            selectedOptions={selectedOrganisationUnits}
            onOptionsSelected={(keys: SelectedOrganisationTreeNode[]) => {
              changeSelectedOrganisationUnits(keys);
            }}
          />
        </OrganisationUnitTreeProvider.All>
        <DateTimeRangeFilterChip
          key={'faults-history-filter-chip-createdOn'}
          placeHolder={t('filters.createdOn')}
          timeRange={30}
          minimumStartDate={moment().subtract(1, 'years')}
          initialDateFilters={filters.dateFilters}
          onDateFiltersChanged={(newDateFilters) => setDateFilters(newDateFilters)}
        />
        <FilterChip
          key={`faults-history-filter-chip-status`}
          options={statusOptions}
          placeHolder={t('filters.status')}
          initialSelectedOption={selectedStatusOption}
          onOptionSelected={(keys?: string) => selectOption(FaultsHistoryFilterValueType.Status, keys ? [keys] : [])}
        />
        <div className={styles.clearFilters}>
          <Button variant="text" color="primary" onClick={clearFilters}>
            {t('filters.clearFilters')}
          </Button>
        </div>
      </div>
      {refresh && (
        <IconButton className={styles.refreshButton} onClick={refresh}>
          <RefreshRounded />
        </IconButton>
      )}
    </div>
  );
};

export default FaultsHistoryFilters;
